import { z } from "zod";

import { buildNamespace } from "@smart/itops-types-basic";

import { system } from "./base";
import { TeamNS } from "./team";
import { UserNS } from "./user";
import { arrayComparer, entriesOf } from "@smart/itops-utils-basic";

export const FormNS = buildNamespace({
  system,
  entity: "Form",
} as const);

export const formCategoryValue = ["lead", "matter"] as const;
export const formCategoryLabel: Record<
  (typeof formCategoryValue)[number],
  string
> = { matter: "Matter", lead: "Lead" };

export type BadgeTheme =
  | "neutral"
  | "emphasis"
  | "highlight"
  | "branding"
  | "success"
  | "warning"
  | "destructive"
  | "information"
  | "pink"
  | "purple";

export const formTypeTheme: Record<"matter" | "lead", BadgeTheme> = {
  matter: "branding",
  lead: "warning",
};
export const formCategorySchema = z.enum(formCategoryValue);

export type FormCategory = (typeof formCategoryValue)[number];

export const aiFillSettingsSchema = z.object({
  allowAiFill: z.boolean().optional(),
  sectionTitle: z.string().optional(),
  fileUploadLabel: z.string().optional(),
  fileUploadDescription: z.string().optional(),
});

export const FormSchema = z.object({
  uri: FormNS.schema,
  operationId: z.string(),

  teamUri: TeamNS.schema,
  createdBy: UserNS.schema,
  createdAt: z.string(),
  updatedBy: UserNS.schema,
  updatedAt: z.string(),

  title: z.string(),
  response: z.string(),
  active: z.boolean().optional(),
  creationStatus: z.string().optional().nullable(),
  source: z.string().optional(),
  matterId: z.string().optional(),
  aiFillSettings: aiFillSettingsSchema.optional(),

  category: formCategorySchema,

  deleted: z.boolean().optional(),
  deletedAtSec: z.number().optional(),
});

export const defaultAIFillSettings = {
  sectionTitle: "Autofill Assistant",
  fileUploadLabel: "**Welcome!**",
  fileUploadDescription: `To autofill parts of this form, upload relevant documents like financial statements, passports, or legal papers.
 
  This step is optional - skip if you'd rather fill it out manually.`,
};

export const familyProQuestionnaireFormUri = "family-pro-questionnaire-form";
export const familyProIntakeEnquiryFormUri = "family-pro-intake-enquiry";
export const familyProParentingFormUri = "family-pro-parenting-questionnaire";
export const familyProParentingAndPropertyFormUri =
  "family-pro-parenting-and-property";
export const familyProPropertyFormUri = "family-pro-property";
export const familyProParentingExclCurrSitFormUri =
  "family-pro-parenting-excluding-current-situation-questionnaire";
export const familyProParentingAndPropertyExclCurrSitFormUri =
  "family-pro-parenting-and-property-excluding-current-situation";
export const familyProPropertyExclCurrSitFormUri =
  "family-pro-property-excluding-current-situation";
export const familyProBalanceSheetIntakeFormUri =
  "family-pro-balance-sheet-intake";
export const familyProBalanceSheetIntakeBalanceSheetFormUri =
  "family-pro-balance-sheet-intake-balance-sheet";
export const familyProIlFinancialAffidavitFormUri =
  "family-pro-il-financial-affidavit";
export const familyProIlFinancialAffidavitBalanceSheetIntakeFormUri =
  "family-pro-il-financial-affidavit-balance-sheet-intake";
export const familyProIlFinancialAffidavitBalanceSheetIntakeBalanceSheetFormUri =
  "family-pro-il-financial-affidavit-balance-sheet-intake-balance-sheet";

export const isFamilyProFormUri = (uri: string | undefined) => {
  if (!uri) return false;
  return [
    familyProIntakeEnquiryFormUri,
    familyProParentingFormUri,
    familyProParentingAndPropertyFormUri,
    familyProPropertyFormUri,
    familyProParentingExclCurrSitFormUri,
    familyProParentingAndPropertyExclCurrSitFormUri,
    familyProPropertyExclCurrSitFormUri,
    familyProBalanceSheetIntakeFormUri,
    familyProBalanceSheetIntakeBalanceSheetFormUri,
    familyProIlFinancialAffidavitFormUri,
    familyProIlFinancialAffidavitBalanceSheetIntakeFormUri,
    familyProIlFinancialAffidavitBalanceSheetIntakeBalanceSheetFormUri,
  ].includes(uri);
};

export type FamilyProFormUri =
  | typeof familyProIntakeEnquiryFormUri
  | typeof familyProParentingFormUri
  | typeof familyProParentingAndPropertyFormUri
  | typeof familyProPropertyFormUri
  | typeof familyProParentingExclCurrSitFormUri
  | typeof familyProParentingAndPropertyExclCurrSitFormUri
  | typeof familyProPropertyExclCurrSitFormUri
  | typeof familyProBalanceSheetIntakeFormUri
  | typeof familyProBalanceSheetIntakeBalanceSheetFormUri
  | typeof familyProIlFinancialAffidavitFormUri
  | typeof familyProIlFinancialAffidavitBalanceSheetIntakeFormUri
  | typeof familyProIlFinancialAffidavitBalanceSheetIntakeBalanceSheetFormUri;

export type AuFamilyProQuestionnaireType =
  | "intakeEnquiry"
  | "parenting"
  | "parentingProperty"
  | "property"
  | "parentingExclCurrSit"
  | "parentingPropertyExclCurrSit"
  | "propertyExclCurrSit";

export type UsFamilyProQuestionnaireType =
  | "balanceSheetIntake"
  | "balanceSheetIntakeBalanceSheet"
  | "ilFinancialAffidavit"
  | "ilFinancialAffidavitBalanceSheetIntake"
  | "ilFinancialAffidavitBalanceSheetIntakeBalanceSheet";

export type FamilyProQuestionnaireType =
  | AuFamilyProQuestionnaireType
  | UsFamilyProQuestionnaireType;

export const extractFamilyProQuestionnaireType = (
  uri: FamilyProFormUri,
): FamilyProQuestionnaireType => {
  switch (uri) {
    case familyProIntakeEnquiryFormUri:
      return "intakeEnquiry";
    case familyProParentingFormUri:
      return "parenting";
    case familyProParentingAndPropertyFormUri:
      return "parentingProperty";
    case familyProPropertyFormUri:
      return "property";
    case familyProParentingExclCurrSitFormUri:
      return "parentingExclCurrSit";
    case familyProParentingAndPropertyExclCurrSitFormUri:
      return "parentingPropertyExclCurrSit";
    case familyProPropertyExclCurrSitFormUri:
      return "propertyExclCurrSit";
    case familyProBalanceSheetIntakeFormUri:
      return "balanceSheetIntake";
    case familyProBalanceSheetIntakeBalanceSheetFormUri:
      return "balanceSheetIntakeBalanceSheet";
    case familyProIlFinancialAffidavitFormUri:
      return "ilFinancialAffidavit";
    case familyProIlFinancialAffidavitBalanceSheetIntakeFormUri:
      return "ilFinancialAffidavitBalanceSheetIntake";
    case familyProIlFinancialAffidavitBalanceSheetIntakeBalanceSheetFormUri:
      return "ilFinancialAffidavitBalanceSheetIntakeBalanceSheet";
    default:
      return "intakeEnquiry";
  }
};

export const questionnaireTypeToFamilyProFormUriMap: Record<
  FamilyProQuestionnaireType,
  FamilyProFormUri
> = {
  intakeEnquiry: familyProIntakeEnquiryFormUri,
  parenting: familyProParentingFormUri,
  parentingProperty: familyProParentingAndPropertyFormUri,
  property: familyProPropertyFormUri,
  parentingExclCurrSit: familyProParentingExclCurrSitFormUri,
  parentingPropertyExclCurrSit: familyProParentingAndPropertyExclCurrSitFormUri,
  propertyExclCurrSit: familyProPropertyExclCurrSitFormUri,
  balanceSheetIntake: familyProBalanceSheetIntakeFormUri,
  balanceSheetIntakeBalanceSheet:
    familyProBalanceSheetIntakeBalanceSheetFormUri,
  ilFinancialAffidavit: familyProIlFinancialAffidavitFormUri,
  ilFinancialAffidavitBalanceSheetIntake:
    familyProIlFinancialAffidavitBalanceSheetIntakeFormUri,
  ilFinancialAffidavitBalanceSheetIntakeBalanceSheet:
    familyProIlFinancialAffidavitBalanceSheetIntakeBalanceSheetFormUri,
};

type FamilyProStepsName =
  | "generalSteps"
  | "propertySteps"
  | "parentingSteps"
  | "currentSituationSteps"
  | "otherSteps";

type FamilyProSteps = {
  [key in FamilyProStepsName]?: number[];
};

const generalSteps = [1, 2, 3, 4];
const propertySteps = [5, 6, 7, 8, 9, 10, 11];
const parentingSteps = [5];
const currentSituationSteps = [1, 2, 3, 4];
const otherSteps = [12];

export const familyProStepsMapping: Record<
  AuFamilyProQuestionnaireType,
  FamilyProSteps
> = {
  intakeEnquiry: { generalSteps },
  parenting: {
    generalSteps,
    currentSituationSteps,
    parentingSteps,
    otherSteps,
  },
  parentingProperty: {
    generalSteps,
    propertySteps,
    currentSituationSteps,
    parentingSteps,
    otherSteps,
  },
  property: {
    generalSteps,
    propertySteps,
    currentSituationSteps,
    otherSteps,
  },
  parentingExclCurrSit: {
    generalSteps,
    parentingSteps,
    otherSteps,
  },
  parentingPropertyExclCurrSit: {
    generalSteps,
    propertySteps,
    parentingSteps,
    otherSteps,
  },
  propertyExclCurrSit: {
    generalSteps,
    propertySteps,
    otherSteps,
  },
};

const usFinancialIntakeV11 = "US_Financial_Intake_v11";
const usBalanceSheet = "US_Balancesheet";
const ilFinancialAffidavitV2 = "IL_Financial_Affidavit_v2";

export const usFamilyProPlaybooksMapping: Record<
  UsFamilyProQuestionnaireType,
  string[]
> = {
  balanceSheetIntake: [usFinancialIntakeV11],
  balanceSheetIntakeBalanceSheet: [usFinancialIntakeV11, usBalanceSheet],
  ilFinancialAffidavit: [ilFinancialAffidavitV2],
  ilFinancialAffidavitBalanceSheetIntake: [
    ilFinancialAffidavitV2,
    usFinancialIntakeV11,
  ],
  ilFinancialAffidavitBalanceSheetIntakeBalanceSheet: [
    ilFinancialAffidavitV2,
    usFinancialIntakeV11,
    usBalanceSheet,
  ],
};

export const usFamilyProMatterTypesWithAdditionalQuestionnaire = [
  "aa94e6d1-661b-4edd-9e47-a74c57bebaee_IL",
  "a1908749-2ad0-423b-a0c2-f425250a3fdf",
  "5acbe508-e618-4615-bbe6-40ef696cf425",
  "a8b7f5b8-3d6b-4815-8bc7-8d4dca787690",
  "e02e7bb0-944d-4f05-b23c-001a25b48144",
  "30cb87fa-267b-4beb-a7a8-75576979eee4",
  "5182e436-9fbd-423b-b27a-3a0060d38ec5",
  "f40e18fc-fe0e-4a41-90ca-4b15fe3e92fa",
];

export const getFamilyProQuestionnaireTypeFromSteps = (
  stepsObject: FamilyProSteps,
): FamilyProQuestionnaireType | undefined => {
  const nonEmptySteps = Object.entries(stepsObject).filter(
    ([, steps]) => steps?.length,
  );

  if (!nonEmptySteps.length) return;

  const found = entriesOf(familyProStepsMapping).find(([, targetSteps]) => {
    const targetStepsEntries = Object.entries(targetSteps);
    if (targetStepsEntries.length !== nonEmptySteps.length) return false;

    return targetStepsEntries.every(([stepsName, steps]) => {
      const correspondingSteps = stepsObject[stepsName as FamilyProStepsName];
      return (
        correspondingSteps &&
        arrayComparer(steps, "equalTo", correspondingSteps)
      );
    });
  });

  return found?.[0];
};

export type FormAIFillSettings = z.infer<typeof aiFillSettingsSchema>;

export const usIntakeTemplateId = "usa_intake_template";
